import { Bell, Home, MessageCircle, User, type LucideIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { CountBadge } from '@/components/ui'
import { cn } from '@/components/ui/cn'
import { UNMASK_AREA } from '@/lib/analytics'
import { useAuth } from '@/lib/auth'
import { lastCommunity } from '@/lib/community'
import { t } from '@/lib/i18n'

function isEditable(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable
}

/** True while the on screen keyboard is likely open (an editable field has focus on a touch screen). */
function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
    if (!coarse) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const update = () => setOpen(isEditable(document.activeElement))
    const onOut = () => {
      clearTimeout(timer)
      timer = setTimeout(update, 50)
    }
    document.addEventListener('focusin', update)
    document.addEventListener('focusout', onOut)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('focusin', update)
      document.removeEventListener('focusout', onOut)
    }
  }, [])
  return open
}

type Item = { key: string; to: string; label: string; icon: LucideIcon; count?: number; active: boolean }

/** Bottom bar below 768 px for members: Home, Chat, Notifications, Profile (UI.md section 3). */
export function MobileBottomNav({ communitySlug }: { communitySlug: string | null }) {
  const { me } = useAuth()
  const { pathname } = useLocation()
  const keyboardOpen = useKeyboardOpen()
  if (!me || keyboardOpen) return null
  const home = communitySlug ?? lastCommunity()
  const items: Item[] = [
    { key: 'home', to: home ? `/${home}` : '/', label: t('Home'), icon: Home, active: !!communitySlug || pathname === '/' },
    { key: 'chat', to: '/chat', label: t('Chat'), icon: MessageCircle, count: me.unread.chats, active: pathname.startsWith('/chat') },
    {
      key: 'notifications',
      to: '/notifications',
      label: t('Notifications'),
      icon: Bell,
      count: me.unread.notifications,
      active: pathname.startsWith('/notifications'),
    },
    { key: 'profile', to: `/u/${me.user.handle}`, label: t('Profile'), icon: User, active: pathname === `/u/${me.user.handle}` },
  ]
  return (
    <nav
      aria-label={t('Main')}
      {...UNMASK_AREA}
      data-mobile-bottom-nav=""
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <ul className="grid h-14 grid-cols-4">
        {items.map(({ key, to, label, icon: Icon, count, active }) => (
          <li key={key}>
            <Link
              to={to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'focus-ring flex h-full flex-col items-center justify-center gap-0.5 text-micro transition-colors duration-150',
                active ? 'text-primary' : 'text-muted hover:text-text',
              )}
            >
              <span className="relative">
                <Icon size={22} strokeWidth={active ? 2 : 1.75} aria-hidden="true" />
                {count ? <CountBadge count={count} className="absolute -top-1.5 left-3.5" /> : null}
              </span>
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}
